/**
 * useComplianceLens - Custom Hook for Privacy Compliance Lens
 * ============================================================================
 * DC-821: Hook for fetching and managing Privacy Compliance Lens data
 *
 * Features:
 * - Fetch latest compliance assessment
 * - Run new assessment
 * - Loading and error states
 * - Auto-refresh capability
 *
 * @version 1.0.0
 * @created 2025-12-26
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export type ComplianceLevel = 'compliant' | 'partial' | 'non_compliant' | 'not_applicable';

export interface FindingsSummary {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  total: number;
}

export interface ComplianceLensResult {
  id: string;
  journey_id: string;
  overall_score: number;
  overall_level: ComplianceLevel;
  gdpr_score?: number;
  gdpr_level?: string;
  hipaa_score?: number;
  hipaa_level?: string;
  ccpa_score?: number;
  ccpa_level?: string;
  findings_summary: FindingsSummary;
  prioritized_recommendations: string[];
  created_at: string;
}

export interface ComplianceLensResponse {
  success: boolean;
  result?: ComplianceLensResult;
  meta?: {
    complianceLevelDescription: string;
    complianceLevelColor: string;
    privacyDataAvailable?: boolean;
  };
  error?: string;
}

export interface UseComplianceLensOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Polling interval in ms (0 to disable) */
  pollInterval?: number;
}

export interface UseComplianceLensReturn {
  /** Latest compliance result */
  result: ComplianceLensResult | null;
  /** Whether data is loading */
  loading: boolean;
  /** Error message if any */
  error: Error | null;
  /** Whether an assessment is currently running */
  isRunning: boolean;
  /** Meta information from API */
  meta: ComplianceLensResponse['meta'] | null;
  /** Fetch the latest result */
  fetchResult: () => Promise<void>;
  /** Run a new assessment */
  runAssessment: () => Promise<ComplianceLensResult | null>;
  /** Clear error state */
  clearError: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useComplianceLens(
  journeyId: string,
  options: UseComplianceLensOptions = {}
): UseComplianceLensReturn {
  const { autoFetch = true, pollInterval = 0 } = options;

  const [result, setResult] = useState<ComplianceLensResult | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [meta, setMeta] = useState<ComplianceLensResponse['meta'] | null>(null);

  /**
   * Fetch the latest compliance assessment result
   */
  const fetchResult = useCallback(async () => {
    if (!journeyId) {
      setError(new Error('Journey ID is required'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/v1/lenses/privacy-compliance/${journeyId}`);

      if (response.status === 404) {
        // No assessment yet - this is not an error
        setResult(null);
        setMeta(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch compliance assessment: ${response.statusText}`);
      }

      const data: ComplianceLensResponse = await response.json();

      if (data.success && data.result) {
        setResult(data.result);
        setMeta(data.meta || null);
      } else {
        setResult(null);
        setMeta(null);
      }
    } catch (err) {
      console.error('[useComplianceLens] Fetch error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  /**
   * Run a new compliance assessment
   */
  const runAssessment = useCallback(async (): Promise<ComplianceLensResult | null> => {
    if (!journeyId) {
      setError(new Error('Journey ID is required'));
      return null;
    }

    try {
      setIsRunning(true);
      setError(null);

      const response = await fetch(`/api/v1/lenses/privacy-compliance/${journeyId}/assess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to run compliance assessment: ${response.statusText}`);
      }

      const data: ComplianceLensResponse = await response.json();

      if (data.success && data.result) {
        setResult(data.result);
        setMeta(data.meta || null);
        return data.result;
      }

      return null;
    } catch (err) {
      console.error('[useComplianceLens] Assessment error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    } finally {
      setIsRunning(false);
    }
  }, [journeyId]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && journeyId) {
      fetchResult();
    }
  }, [journeyId, autoFetch, fetchResult]);

  // Polling
  useEffect(() => {
    if (pollInterval > 0 && journeyId) {
      const intervalId = setInterval(() => {
        fetchResult();
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
  }, [pollInterval, journeyId, fetchResult]);

  return {
    result,
    loading,
    error,
    isRunning,
    meta,
    fetchResult,
    runAssessment,
    clearError,
  };
}

export default useComplianceLens;
